import * as Types from '../types-api.generated';

import { MediaFragment, EditionSaleConfigurationFragment, DropSaleConfigurationFragment } from '../api-fragments.generated';
import { useQuery, useInfiniteQuery, UseQueryOptions, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query';
import { apiFetcher } from 'lib/clients/graphql';

import { ClientError } from "graphql-request";
import { Address } from "viem";
import { ChainId } from "lib/chains";

export type ApiExhibitionFeaturedItemsVariables = Types.Exact<{
  id: Types.Scalars['Int'];
  page?: Types.InputMaybe<Types.Scalars['Int']>;
  perPage?: Types.InputMaybe<Types.Scalars['Int']>;
}>;


export type ApiExhibitionFeaturedItems = { exhibitionFeaturedItems: { totalItems: number, page: number, totalPages: number, items: Array<{ __typename: 'DropCollection', id: string, name: string, mintPrice: number | null, clearingPrice: number | null, saleType: Types.ApiCollectionSaleType | null, description: string | null, slug: string, earlyAccessStartTime: string | null, generalAvailabilityStartTime: string | null, contractType: Types.ApiContractType, chainId: ChainId, contractAddress: Address, isRevealed: boolean | null, nftCount: number, status: Types.ApiDropAvailabilityStatus, totalSales: number, media: { __typename: 'ImageMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, imageMimeType: Types.ApiImageMimeType } | { __typename: 'ModelMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, modelMimeType: Types.ApiModelMimeType, modelStaticUrl: string } | { __typename: 'VideoMedia', previewUrl: string | null, processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, staticUrl: string | null, url: string, videoMimeType: Types.ApiVideoMimeType } | null, creator: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address }, saleConfiguration: { __typename: 'FixedPriceSaleConfiguration', maxTokenId: number, mintPrice: number, presaleStartTime: string | null, startTime: string, fndFixedPriceSaleStatus: Types.ApiFixedPriceStatus } | { __typename: 'HighlightFixedPriceSaleConfiguration', highlightId: string, mintPrice: number, startTime: string, optionalEndTime: string | null, optionalMaxTokenId: number | null, vectorId: number, highlightFixedPriceSaleStatus: Types.ApiHighlightFixedPriceStatus } | { __typename: 'HighlightLinearDutchAuctionSaleConfiguration' } | { __typename: 'HighlightStaggeredDutchAuctionSaleConfiguration', clearingPrice: number | null, endTime: string, highlightId: string, mintVectorId: string, maxPrice: number, maxTokenId: number, minPrice: number, mintEndTime: string | null, startTime: string, highlightStaggeredDutchAuctionStatus: Types.ApiHighlightDutchAuctionStatus, prices: Array<{ endTime: string, price: number }> } | { __typename: 'LinearDutchAuctionSaleConfiguration', clearingPrice: number | null, endTime: string, maxPrice: number, maxTokenId: number, minPrice: number, startTime: string, fndLinearDutchAuctionStatus: Types.ApiDutchAuctionStatus } | null } | { __typename: 'EditionCollection', nftCount: number, description: string | null, name: string, slug: string, chainId: ChainId, contractAddress: Address, contractType: Types.ApiContractType, editionCollectionId: string, editionMimeType: string | null, editionCollectionAssetUrl: string | null, totalSales: number, creator: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address }, editionSale: { __typename: 'LimitedEditionSaleConfiguration', maxTokenId: number, mintPrice: number, startTime: string, limitedEditionStatus: Types.ApiLimitedEditionStatus } | { __typename: 'TimedEditionSaleConfiguration', endTime: string, mintPrice: number, startTime: string, timedEditionStatus: Types.ApiTimedEditionStatus } } | { __typename: 'MarketNft', chainId: ChainId, tokenId: number, description: string | null, nftName: string | null, collectionContractAddress: Address, nftSaleStartsAt: string | null, activeBuyNow: { amount: number } | null, activeOffer: { expiryDate: string, amount: number, publicKey: Address } | null, activeAuction: { id: number, state: Types.ApiAuctionState, expiryDate: string | null, reservePrice: number, duration: number, highestBid: { amount: number, bidder: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address } } | null } | null, lastSale: { amount: number, date: string, worldId: number | null, buyer: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address } } | null, media: { __typename: 'ImageMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, imageMimeType: Types.ApiImageMimeType } | { __typename: 'ModelMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, modelMimeType: Types.ApiModelMimeType, modelStaticUrl: string } | { __typename: 'VideoMedia', previewUrl: string | null, processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, staticUrl: string | null, url: string, videoMimeType: Types.ApiVideoMimeType } | null, creator: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address } } | { __typename: 'Moment', description: string | null, worldId: number, name: string, posterUrl: string, startsAt: string, takeRateInBasisPoints: number, momentId: number }> } };



export const ApiExhibitionFeaturedItemsDocument = /*#__PURE__*/ `
    query ExhibitionFeaturedItems($id: Int!, $page: Int, $perPage: Int) {
  exhibitionFeaturedItems(exhibitionId: $id, page: $page, perPage: $perPage) {
    totalItems
    page
    totalPages
    items {
      __typename
      ... on MarketNft {
        chainId
        nftName: name
        tokenId
        description
        collectionContractAddress: contractAddress
        activeBuyNow {
          amount
        }
        activeOffer {
          expiryDate
          publicKey: accountAddress
          amount
        }
        activeAuction {
          id
          state
          expiryDate
          reservePrice
          duration
          highestBid {
            amount
            bidder {
              profileImageUrl
              publicKey: accountAddress
              username
              name
            }
          }
        }
        nftSaleStartsAt: saleStartsAt
        lastSale {
          amount
          date
          worldId
          buyer {
            profileImageUrl
            publicKey: accountAddress
            username
            name
          }
        }
        media {
          ...MediaFragment
        }
        creator {
          profileImageUrl
          publicKey: accountAddress
          username
          name
        }
      }
      ... on EditionCollection {
        nftCount
        editionCollectionId: id
        description
        editionMimeType: mimeType
        name
        editionCollectionAssetUrl: assetUrl
        slug
        chainId
        contractAddress
        totalSales: totalVolumeInEth
        contractType
        creator {
          profileImageUrl
          publicKey: accountAddress
          username
          name
        }
        editionSale: saleConfiguration {
          ...EditionSaleConfigurationFragment
        }
      }
      ... on DropCollection {
        id
        name
        mintPrice
        clearingPrice
        saleType
        status: availabilityStatus
        description
        slug
        earlyAccessStartTime
        generalAvailabilityStartTime
        contractType
        chainId
        contractAddress
        totalSales: totalVolumeInEth
        isRevealed
        media {
          ...MediaFragment
        }
        creator {
          profileImageUrl
          publicKey: accountAddress
          username
          name
        }
        nftCount
        saleConfiguration {
          ...DropSaleConfigurationFragment
        }
      }
      ... on Moment {
        description
        worldId
        momentId: id
        name
        posterUrl
        startsAt
        takeRateInBasisPoints
      }
    }
  }
}
    ${MediaFragment}
${EditionSaleConfigurationFragment}
${DropSaleConfigurationFragment}`;

export const useExhibitionFeaturedItems = <
      TData = ApiExhibitionFeaturedItems,
      TError = ClientError
    >(
      variables: ApiExhibitionFeaturedItemsVariables,
      options?: Omit<UseQueryOptions<ApiExhibitionFeaturedItems, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<ApiExhibitionFeaturedItems, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<ApiExhibitionFeaturedItems, TError, TData>(
      {
    queryKey: ['ExhibitionFeaturedItems', variables],
    queryFn: apiFetcher<ApiExhibitionFeaturedItems, ApiExhibitionFeaturedItemsVariables>(ApiExhibitionFeaturedItemsDocument, variables),
    ...options
  }
    )};

useExhibitionFeaturedItems.getKey = (variables: ApiExhibitionFeaturedItemsVariables) => ['ExhibitionFeaturedItems', variables];

export const useInfiniteExhibitionFeaturedItems = <
      TData = InfiniteData<ApiExhibitionFeaturedItems>,
      TError = ClientError
    >(
      variables: ApiExhibitionFeaturedItemsVariables,
      options: Omit<UseInfiniteQueryOptions<ApiExhibitionFeaturedItems, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<ApiExhibitionFeaturedItems, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<ApiExhibitionFeaturedItems, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['ExhibitionFeaturedItems.infinite', variables],
      queryFn: (metaData) => apiFetcher<ApiExhibitionFeaturedItems, ApiExhibitionFeaturedItemsVariables>(ApiExhibitionFeaturedItemsDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

useInfiniteExhibitionFeaturedItems.getKey = (variables: ApiExhibitionFeaturedItemsVariables) => ['ExhibitionFeaturedItems.infinite', variables];


useExhibitionFeaturedItems.fetcher = (variables: ApiExhibitionFeaturedItemsVariables, options?: RequestInit['headers']) => apiFetcher<ApiExhibitionFeaturedItems, ApiExhibitionFeaturedItemsVariables>(ApiExhibitionFeaturedItemsDocument, variables, options);
